import { useCustom } from "@refinedev/core";
import { Modal, Spin, Typography } from "antd";
import { apiUrl } from "@/consts";

const { Text } = Typography;

type PlayerCardData = {
  id: string;
  imageUrl: string;
  rank: number;
  isPinned: boolean;
  revealed: boolean;
  playerName: string | null;
  playerEmail: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Pass when the caller already has the rendered image (e.g. grid tile). */
  imageUrl?: string;
  /** Pass eventId+userId when the modal needs to fetch on open (e.g. survey list). */
  eventId?: string;
  userId?: string;
  playerName?: string | null;
  playerEmail?: string | null;
};

export function PlayerCardModal({
  open,
  onClose,
  imageUrl,
  eventId,
  userId,
  playerName,
  playerEmail,
}: Props) {
  const needsFetch = open && !imageUrl && !!eventId && !!userId;

  const { data, isLoading, isError, error } = useCustom<{ data: PlayerCardData }>({
    url: `${apiUrl}/scouting-event/${eventId}/player-card/${userId}/preview`,
    method: "get",
    queryOptions: { enabled: needsFetch },
  });

  const card = data?.data?.data;
  const resolvedImageUrl = imageUrl ?? card?.imageUrl ?? null;
  const resolvedName = playerName ?? card?.playerName ?? null;
  const resolvedEmail = playerEmail ?? card?.playerEmail ?? null;

  const title =
    resolvedName || resolvedEmail
      ? `${resolvedName ?? "Player"}${resolvedEmail ? ` — ${resolvedEmail}` : ""}`
      : "Player Card";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={title}
      width={520}
      centered
      destroyOnClose
    >
      {needsFetch && isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : isError ? (
        <Text type="danger">
          {(error as { message?: string } | null)?.message ?? "Failed to load player card"}
        </Text>
      ) : resolvedImageUrl ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <img
            src={resolvedImageUrl}
            alt={resolvedName ?? "Player card"}
            style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}
          />
        </div>
      ) : (
        <Text type="secondary">No card image available.</Text>
      )}
    </Modal>
  );
}
